import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { loadSnap } from "@/lib/midtrans";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";

const QUICK_AMOUNTS = [50000, 100000, 250000, 500000, 1000000, 5000000];
const PROGRAMS = [
  "Umum",
  "Humanity & Resilience",
  "Food Systems & Bioeconomy",
  "Health & Nutrition",
  "Education & Leadership",
  "Green Impact & Climate Action",
  "Economic Empowerment",
  "Future Innovation",
];

function formatIDR(n) {
  const num = Number(n) || 0;
  return "Rp " + num.toLocaleString("id-ID");
}

export default function DonationForm() {
  const [config, setConfig] = useState(null);
  const [amount, setAmount] = useState(100000);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [program, setProgram] = useState("Umum");
  const [message, setMessage] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { order_id, status }

  useEffect(() => {
    api.get("/donation/config")
      .then((r) => setConfig(r.data))
      .catch((e) => console.error("Config error", e));
  }, []);

  const disabled = useMemo(() => {
    return !config?.enabled || submitting || amount < (config?.min_amount || 10000);
  }, [config, submitting, amount]);

  const openSnap = async (token, orderId) => {
    try {
      const snap = await loadSnap({
        clientKey: config.client_key,
        isProduction: config.is_production,
      });
      snap.pay(token, {
        onSuccess: () => {
          toast.success("Pembayaran berhasil! Terima kasih atas donasi Anda.");
          setResult({ order_id: orderId, status: "success" });
        },
        onPending: () => {
          toast.info("Pembayaran tertunda — cek status transaksi Anda.");
          setResult({ order_id: orderId, status: "pending" });
        },
        onError: (err) => {
          console.error("Snap error", err);
          toast.error("Pembayaran gagal. Silakan coba lagi.");
          setResult({ order_id: orderId, status: "failed" });
        },
        onClose: () => {
          toast.info("Anda menutup popup pembayaran sebelum menyelesaikan transaksi.");
          setResult({ order_id: orderId, status: "pending" });
        },
      });
    } catch (e) {
      console.error(e);
      toast.error("Gagal memuat popup pembayaran.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (amount < (config?.min_amount || 10000)) {
      toast.error(`Minimum donasi ${formatIDR(config?.min_amount || 10000)}`);
      return;
    }
    setSubmitting(true);
    try {
      const donorName = isAnonymous ? "Anonim" : (name.trim() || "Anonim");
      const { data } = await api.post("/donation/create", {
        name: donorName,
        email: email.trim() || undefined,
        phone: phone.trim(),
        amount: Number(amount),
        program,
        message: message.trim(),
        is_anonymous: isAnonymous,
      });
      await openSnap(data.snap_token, data.order_id);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!config) {
    return (
      <div className="card-sf p-8 flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!config.enabled) {
    return (
      <div className="card-sf p-6 border-l-4 border-amber-400 bg-amber-50">
        <div className="text-sm font-semibold text-amber-900">Payment gateway belum aktif</div>
        <div className="mt-1 text-xs text-amber-800">Silakan transfer melalui rekening bank di bawah.</div>
      </div>
    );
  }

  if (result) {
    const icon = result.status === "success"
      ? <CheckCircle2 className="h-6 w-6 text-emerald-500" />
      : result.status === "pending"
      ? <Clock className="h-6 w-6 text-amber-500" />
      : <XCircle className="h-6 w-6 text-red-500" />;
    const label = result.status === "success"
      ? "Terima kasih! Donasi Anda telah diterima."
      : result.status === "pending"
      ? "Pembayaran menunggu konfirmasi."
      : "Pembayaran tidak berhasil.";
    return (
      <div className="card-sf p-8" data-testid="donation-result">
        <div className="flex items-center gap-3 mb-3">{icon}<div className="text-xl font-bold text-slate-900">{label}</div></div>
        <div className="text-sm text-slate-600">Nomor pesanan: <span className="font-mono font-semibold text-slate-900">{result.order_id}</span></div>
        <div className="mt-6 flex gap-3">
          <button
            onClick={() => { setResult(null); setAmount(100000); setMessage(""); }}
            className="btn-secondary"
            data-testid="donate-again"
          >
            Donasi lagi
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="card-sf p-6 md:p-8 space-y-5" data-testid="donation-form">
      <div className="grid grid-cols-3 gap-2">
        {QUICK_AMOUNTS.map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setAmount(v)}
            data-testid={`amount-${v}`}
            className={`rounded-xl border py-3 text-sm font-semibold transition ${
              amount === v
                ? "border-[var(--brand-orange)] bg-[var(--brand-orange)]/10 text-[var(--brand-orange)]"
                : "border-slate-200 hover:border-slate-400 text-slate-700"
            }`}
          >
            {formatIDR(v)}
          </button>
        ))}
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-700 mb-1.5">Nominal Donasi (IDR) <span className="text-[var(--brand-orange)]">*</span></label>
        <input
          type="number"
          min={config.min_amount}
          step={1000}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          required
          className="w-full px-4 py-3 rounded-xl border border-slate-200 text-base font-semibold focus:outline-none focus:border-[var(--brand-blue)]"
          data-testid="donation-amount"
        />
        <p className="mt-1 text-xs text-slate-500">Minimum {formatIDR(config.min_amount)}</p>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-700 mb-1.5">Program yang didukung</label>
        <select
          value={program}
          onChange={(e) => setProgram(e.target.value)}
          className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[var(--brand-blue)]"
          data-testid="donation-program"
        >
          {PROGRAMS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1.5">Nama {!isAnonymous && <span className="text-[var(--brand-orange)]">*</span>}</label>
          <input
            type="text"
            value={name}
            required={!isAnonymous}
            disabled={isAnonymous}
            onChange={(e) => setName(e.target.value)}
            placeholder={isAnonymous ? "Anonim" : "Nama lengkap"}
            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[var(--brand-blue)] disabled:bg-slate-50"
            data-testid="donation-name"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1.5">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@contoh.com"
            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[var(--brand-blue)]"
            data-testid="donation-email"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-700 mb-1.5">Nomor HP / WhatsApp</label>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="0812xxxxxxxx"
          className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[var(--brand-blue)]"
          data-testid="donation-phone"
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-700 mb-1.5">Pesan / Doa (opsional)</label>
        <textarea
          rows={3}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Tuliskan pesan atau doa Anda..."
          className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[var(--brand-blue)]"
          data-testid="donation-message"
        />
      </div>

      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={isAnonymous}
          onChange={(e) => setIsAnonymous(e.target.checked)}
          className="h-4 w-4"
          data-testid="donation-anonymous"
        />
        Donasikan sebagai Anonim
      </label>

      <button
        type="submit"
        disabled={disabled}
        className="btn-primary w-full py-3.5 justify-center text-base"
        data-testid="donation-submit"
      >
        {submitting ? (
          <><Loader2 className="h-4 w-4 animate-spin" /> Memproses...</>
        ) : (
          <>Donasi Sekarang — {formatIDR(amount)}</>
        )}
      </button>

      <p className="text-xs text-slate-500 text-center">
        Pembayaran diproses oleh Midtrans (Bank Transfer, Kartu Kredit, GoPay, OVO, Dana, ShopeePay, QRIS, dll).
      </p>
    </form>
  );
}
